<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>

    <admin-breadcrumb
        :items="[
          {
            text: 'Reports',
            path: { name: 'admin-reports' }
          }
        ]"
        name="Public Menu Sharing"
        :title="pageTitle"
    />

    <admin-panel with-header>
      <b-table
          :fields="reportColumns"
          :items="reportRows"
          table-class="text-nowrap"
          responsive
          hover
          show-empty
          empty-text="No public menu sharing records found"
      >
        <template #cell(company)="data">
          <div class="font-weight-bold">
            {{ data.item.company_name || '—' }}
          </div>

          <div
              v-if="data.item.state"
              class="small text-muted"
          >
            {{ data.item.state }}
          </div>
        </template>

        <template #cell(user)="data">
          <div>
            {{ data.item.shared_by || '—' }}
          </div>

          <div class="small text-muted">
            {{ data.item.shared_by_email || '—' }}
          </div>
        </template>

        <template #cell(menu)="data">
          <div class="font-weight-bold">
            {{ data.item.menu_name || '—' }}
          </div>

          <div class="small text-muted">
            {{
              data.item.menu_type === 'company'
                  ? 'Company Menu'
                  : 'Brand Menu'
            }}
          </div>
        </template>

        <template #cell(public_url)="data">
          <b-link
              v-if="
                data.item.status === 'active' &&
                data.item.public_url
              "
              :href="data.item.public_url"
              target="_blank"
              rel="noopener noreferrer"
          >
            Open Menu
            <b-icon-box-arrow-up-right
                class="ml-1"
                font-scale="0.8"
            />
          </b-link>

          <span
              v-else
              class="text-muted"
          >
            —
          </span>
        </template>

        <template #cell(status)="data">
          <b-badge
              :variant="getStatusVariant(data.item.status)"
          >
            {{ getStatusLabel(data.item.status) }}
          </b-badge>
        </template>

        <template #head(first_shared_at)="data">
          <span
              class="sortable-column"
              @click="toggleSort('first_shared_at')"
          >
            {{ data.label }}
            {{ renderSortIcon('first_shared_at') }}
          </span>
        </template>

        <template #head(last_shared_at)="data">
          <span
              class="sortable-column"
              @click="toggleSort('last_shared_at')"
          >
            {{ data.label }}
            {{ renderSortIcon('last_shared_at') }}
          </span>
        </template>

        <template #cell(first_shared_at)="data">
          <div>
            {{ formatDate(data.item.first_shared_at) }}
          </div>

          <div class="small text-muted">
            {{ formatTime(data.item.first_shared_at) }}
          </div>
        </template>

        <template #cell(last_shared_at)="data">
          <div>
            {{ formatDate(data.item.last_shared_at) }}
          </div>

          <div class="small text-muted">
            {{ formatTime(data.item.last_shared_at) }}
          </div>

          <div
              v-if="
                data.item.generated_by &&
                data.item.generated_by !== data.item.shared_by
              "
              class="small text-muted mt-1"
          >
            by {{ data.item.generated_by }}
          </div>
        </template>
      </b-table>

      <template v-slot:header>
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex flex-wrap align-items-center">
            <div class="mr-3 my-1">
              <strong class="text-uppercase">
                Start Date:
              </strong>

              {{ startDate }}

              <b-form-datepicker
                  v-model="filters.start_date"
                  class="ml-2"
                  :max="maxDate"
                  button-only
                  button-variant="primary"
                  reset-button
                  @input="onStartDateChanged"
              />
            </div>

            <div class="my-1">
              <strong class="text-uppercase">
                End Date:
              </strong>

              {{ endDate }}

              <b-form-datepicker
                  v-model="filters.end_date"
                  class="ml-2"
                  :max="maxDate"
                  button-only
                  button-variant="primary"
                  reset-button
                  @input="onEndDateChanged"
              />
            </div>
          </div>

          <div class="d-flex">
            <b-btn
                variant="primary"
                class="mr-2"
                v-b-tooltip.hover
                title="Download CSV"
                :disabled="!reportRows.length"
                @click.prevent="downloadCsv"
            >
              <b-icon-download/>
            </b-btn>

            <b-btn
                variant="primary"
                v-b-tooltip.hover
                title="Reset Filters"
                @click.prevent="resetFilters"
            >
              <b-icon-arrow-counterclockwise/>
            </b-btn>
          </div>
        </div>
      </template>

      <template v-slot:header-right>
        <div class="mt-n2 d-flex flex-wrap align-items-center">
          <b-input-group
              class="mr-2 my-2 public-menu-sharing-search"
          >
            <b-form-input
                v-model.trim="filters.search"
                placeholder="Company, user or email"
                @keyup.enter="loadReportData"
            />

            <b-input-group-append>
              <b-btn
                  variant="primary"
                  @click="loadReportData"
              >
                Search
              </b-btn>
            </b-input-group-append>
          </b-input-group>

          <b-form-select
              class="w-auto mr-2 my-2"
              v-model="filters.state"
              :options="stateOptions"
              @change="loadReportData"
          />

          <b-form-select
              class="w-auto mr-2 my-2"
              v-model="filters.status"
              :options="statusOptions"
              @change="loadReportData"
          />
        </div>
      </template>
    </admin-panel>
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import moment from "moment";
import {API_ROUTES} from "../api";

export default {
  name: "AdminReportPublicMenuSharing",

  components: {
    AdminPanel,
    AdminBreadcrumb,
    AdminPreloader,
  },

  data() {
    const query = this.$route.query;

    return {
      loading: true,

      report_data: [],

      availableStates: [],

      sortableColumns: [
        "first_shared_at",
        "last_shared_at",
      ],

      filters: {
        /*
         * No date range by default.
         * Requirement says report should show all applicable records.
         */
        start_date:
            query.start_date ??
            null,

        end_date:
            query.end_date ??
            null,

        search:
            query.search ??
            "",

        state:
            query.state ??
            null,

        status:
            query.status ??
            null,

        sort:
            query.sort ??
            "last_shared_at",

        sort_direction:
            query.sort_direction ??
            "desc",
      },
    };
  },

  methods: {
    setQueryVar(key, value) {
      const query = {
        ...this.$route.query,
      };

      if (
          value === null ||
          value === "" ||
          typeof value === "undefined"
      ) {
        delete query[key];
      } else {
        query[key] = value;
      }

      this.$router.push({
        query,
      });
    },

    onStartDateChanged(startDate) {
      if (
          startDate &&
          this.filters.end_date &&
          moment(startDate).isAfter(
              moment(this.filters.end_date)
          )
      ) {
        this.filters.end_date =
            startDate;
      }

      this.loadReportData();
    },

    onEndDateChanged(endDate) {
      if (
          endDate &&
          this.filters.start_date &&
          moment(endDate).isBefore(
              moment(this.filters.start_date)
          )
      ) {
        this.filters.start_date =
            endDate;
      }

      this.loadReportData();
    },

    toggleSort(field) {
      if (
          !this.sortableColumns.includes(field)
      ) {
        return;
      }

      if (
          this.filters.sort === field
      ) {
        this.filters.sort_direction =
            this.filters.sort_direction === "asc"
                ? "desc"
                : "asc";
      } else {
        this.filters.sort =
            field;

        this.filters.sort_direction =
            "desc";
      }

      this.loadReportData();
    },

    renderSortIcon(field) {
      if (
          this.filters.sort !== field
      ) {
        return "";
      }

      return this.filters.sort_direction === "asc"
          ? "↑"
          : "↓";
    },

    loadReportData() {
      const params = {
        start_date:
            this.filters.start_date || null,

        end_date:
            this.filters.end_date || null,

        search:
            this.filters.search || null,

        state:
            this.filters.state || null,

        status:
            this.filters.status || null,

        sort:
        this.filters.sort,

        sort_direction:
        this.filters.sort_direction,
      };

      this.loading = true;

      this.$api
          .get(
              API_ROUTES.ADMIN_REPORT_PUBLIC_MENU_SHARING,
              {
                params,
              }
          )
          .then(({data}) => {
            this.report_data =
                Array.isArray(
                    data.report_data
                )
                    ? data.report_data
                    : [];

            this.availableStates =
                Array.isArray(
                    data.filters?.states
                )
                    ? data.filters.states
                    : [];
          })
          .catch(({response}) => {
            let message =
                "Failed to load Public Menu Sharing report";

            if (
                response &&
                response.status === 422
            ) {
              message =
                  "Some of your params were incorrect";
            }

            this.$bvToast.toast(
                message,
                {
                  title: "Error!",
                  variant: "danger",
                }
            );
          })
          .finally(() => {
            this.loading =
                false;
          });
    },

    resetFilters() {
      this.filters = {
        start_date: null,

        end_date: null,

        search: "",

        state: null,

        status: null,

        sort:
            "last_shared_at",

        sort_direction:
            "desc",
      };

      this.loadReportData();
    },

    getStatusLabel(status) {
      switch (status) {
        case "active":
          return "Active";

        case "inactive":
          return "Inactive";

        default:
          return "Unknown";
      }
    },

    getStatusVariant(status) {
      switch (status) {
        case "active":
          return "success";

        case "inactive":
          return "secondary";

        default:
          return "secondary";
      }
    },

    formatDate(value) {
      if (!value) {
        return "—";
      }

      return moment(value).format(
          "L"
      );
    },

    formatTime(value) {
      if (!value) {
        return "";
      }

      return moment(value).format(
          "LT"
      );
    },

    downloadCsv() {
      if (!this.reportRows.length) {
        return;
      }

      const escapeCsvValue = (value) => {
        const normalized =
            String(value ?? "")
                .replace(/\r\n/g, " ")
                .replace(/\n/g, " ")
                .replace(/\r/g, " ")
                .replace(/"/g, '""')
                .trim();

        return `"${normalized}"`;
      };

      const headers = [
        "Company",
        "User",
        "User Email",
        "State",
        "Public Menu Name",
        "Menu Type",
        "Public Menu URL",
        "Date First Shared",
        "Most Recent Share/Update",
        "Most Recent Generated By",
        "Status",
      ];

      const rows =
          this.reportRows.map(
              (row) => [
                escapeCsvValue(
                    row.company_name
                ),

                escapeCsvValue(
                    row.shared_by
                ),

                escapeCsvValue(
                    row.shared_by_email
                ),

                escapeCsvValue(
                    row.state
                ),

                escapeCsvValue(
                    row.menu_name
                ),

                escapeCsvValue(
                    row.menu_type === "company"
                        ? "Company Menu"
                        : "Brand Menu"
                ),

                escapeCsvValue(
                    row.public_url
                ),

                escapeCsvValue(
                    row.first_shared_at
                        ? moment(
                            row.first_shared_at
                        ).format(
                            "YYYY-MM-DD HH:mm:ss"
                        )
                        : ""
                ),

                escapeCsvValue(
                    row.last_shared_at
                        ? moment(
                            row.last_shared_at
                        ).format(
                            "YYYY-MM-DD HH:mm:ss"
                        )
                        : ""
                ),

                escapeCsvValue(
                    row.generated_by
                ),

                escapeCsvValue(
                    this.getStatusLabel(
                        row.status
                    )
                ),
              ]
          );

      const csvContent = [
        headers
            .map(escapeCsvValue)
            .join(","),

        ...rows.map(
            (row) => row.join(",")
        ),
      ].join("\r\n");

      const blob = new Blob(
          [csvContent],
          {
            type: "text/csv;charset=utf-8;",
          }
      );

      const url =
          URL.createObjectURL(blob);

      const link =
          document.createElement("a");

      link.setAttribute(
          "href",
          url
      );

      link.setAttribute(
          "download",
          "public_menu_sharing.csv"
      );

      document.body.appendChild(
          link
      );

      link.click();

      document.body.removeChild(
          link
      );

      URL.revokeObjectURL(
          url
      );
    },
  },

  computed: {
    pageTitle() {
      return "Public Menu Sharing";
    },

    maxDate() {
      return moment().format(
          "YYYY-MM-DD"
      );
    },

    startDate() {
      if (
          !this.filters.start_date
      ) {
        return "All Time";
      }

      return moment(
          this.filters.start_date
      ).format("L");
    },

    endDate() {
      if (
          !this.filters.end_date
      ) {
        return "All Time";
      }

      return moment(
          this.filters.end_date
      ).format("L");
    },

    stateOptions() {
      return [
        {
          value: null,
          text: "All States",
        },

        ...this.availableStates.map(
            (state) => ({
              value: state,
              text: state,
            })
        ),
      ];
    },

    statusOptions() {
      return [
        {
          value: null,
          text: "All Statuses",
        },
        {
          value: "active",
          text: "Active",
        },
        {
          value: "inactive",
          text: "Inactive",
        },
      ];
    },

    reportColumns() {
      return [
        {
          key: "company",
          label: "Company",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "user",
          label: "Shared By",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "menu",
          label: "Public Menu",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "public_url",
          label: "URL",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "first_shared_at",
          label: "First Shared",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "last_shared_at",
          label: "Last Shared / Updated",
          thClass: "border-top-0",
          tdClass: "align-middle",
        },
        {
          key: "status",
          label: "Status",
          thClass: "border-top-0",
          tdClass: "align-middle text-center",
        },
      ];
    },

    reportRows() {
      if (
          !Array.isArray(
              this.report_data
          )
      ) {
        return [];
      }

      return this.report_data.map(
          (item) => ({
            id:
                item.id ?? null,

            company_id:
                item.company_id ?? null,

            company_name:
                item.company_name ?? "",

            shared_by:
                item.shared_by ?? "",

            shared_by_email:
                item.shared_by_email ?? "",

            generated_by:
                item.generated_by ?? "",

            generated_by_email:
                item.generated_by_email ?? "",

            state:
                item.state ?? "",

            menu_name:
                item.menu_name ?? "",

            menu_type:
                item.menu_type ?? "brand",

            public_url:
                item.public_url ?? null,

            first_shared_at:
                item.first_shared_at ?? null,

            last_shared_at:
                item.last_shared_at ?? null,

            generated_at:
                item.generated_at ?? null,

            status:
                item.status ?? "inactive",
          })
      );
    },
  },

  watch: {
    "filters.start_date": function (
        startDate
    ) {
      this.setQueryVar(
          "start_date",
          startDate
      );
    },

    "filters.end_date": function (
        endDate
    ) {
      this.setQueryVar(
          "end_date",
          endDate
      );
    },

    "filters.search": function (
        search
    ) {
      this.setQueryVar(
          "search",
          search
      );
    },

    "filters.state": function (
        state
    ) {
      this.setQueryVar(
          "state",
          state
      );
    },

    "filters.status": function (
        status
    ) {
      this.setQueryVar(
          "status",
          status
      );
    },

    "filters.sort": function (
        sort
    ) {
      this.setQueryVar(
          "sort",
          sort
      );
    },

    "filters.sort_direction": function (
        sortDirection
    ) {
      this.setQueryVar(
          "sort_direction",
          sortDirection
      );
    },
  },

  created() {
    this.loadReportData();
  },
};
</script>

<style scoped>
.sortable-column {
  cursor: pointer;
  user-select: none;
}

.public-menu-sharing-search {
  width: 320px;
}
</style>